import { useState } from 'react';

import { faCopy } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { getCalendarURL } from 'RestAPIs/calendar';

import {
  Box,
  Button,
  DialogActions,
  IconButton,
  styled,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';

import {
  Dialog,
  FontAwesomeSvgIcon,
  showSnackbar,
  TabPanel
} from 'components/shared';
import StyledTabs from 'components/shared/StyledTabs';

import { CALENDAR_TYPES } from './consts';

const StyledDojoButton = styled(Button)`
  border: 1px solid #ccc;
  padding: 6px 10px;
  color: #818184;
  background: #ebebeb url('aysist/styles/external/img/buttonEnabled.png')
    repeat-x;
  text-transform: none;
  font-size: 13px;
  font-family: 'signikaregular', Arial, sans-serif;
  height: 30px;
`;

const ShareCalendarDialog = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const { t } = useTranslation('common');

  const { data: url = '' } = useQuery(
    ['calendarUrl'],
    async () => {
      const { data } = await getCalendarURL();
      return data;
    },
    {
      enabled: isOpen
    }
  );

  const onClickCopy = async () => {
    await navigator.clipboard.writeText(url);
    showSnackbar({
      severity: 'success',
      message: t('shareCalendar.copiedToClipboard')
    });
  };

  const onClickShareCalendarButton = () => setIsOpen(true);
  const onChangeTab = (e, newValue: number) => {
    setActiveTab(newValue);
  };
  const handleClose = () => setIsOpen(false);

  return (
    <Box display="inline-block">
      <StyledDojoButton
        variant="outlined"
        size="small"
        sx={{ height: '30px' }}
        onClick={onClickShareCalendarButton}
      >
        <span className="promote-tab">{t('shareCalendar.name')}</span>
      </StyledDojoButton>
      <Dialog
        open={isOpen}
        title={t('shareCalendar.name')}
        handleClose={handleClose}
        PaperProps={{
          sx: { minWidth: 600 }
        }}
      >
        <Box>
          <Box display="flex" alignItems="flex-end">
            <TextField
              label={t('shareCalendar.calendarURL')}
              fullWidth
              disabled
              value={url}
            />
            <IconButton aria-label="Copy to clipboard" onClick={onClickCopy}>
              <FontAwesomeSvgIcon icon={faCopy} fontSize="small" />
            </IconButton>
          </Box>
          <Box py={3}>
            <StyledTabs>
              <Tabs
                indicatorColor="primary"
                value={activeTab}
                onChange={onChangeTab}
              >
                {CALENDAR_TYPES.map((type, idx) => (
                  <Tab
                    key={`tab_${type}`}
                    value={idx}
                    label={t(`shareCalendar.types.${type}`)}
                  />
                ))}
              </Tabs>
              <Box>
                {CALENDAR_TYPES.map((type, idx) => (
                  <TabPanel
                    key={`tab_panel_${type}`}
                    index={idx}
                    value={activeTab}
                    title={t(`shareCalendar.types.${type}`)}
                  >
                    <Typography sx={{ mt: 3 }}>
                      {t('shareCalendar.instructions.clickLink')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, fontFamily: 'signikaregular' }}
                    >
                      <a
                        href={t(`shareCalendar.instructions.${type}.url`)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t(`shareCalendar.instructions.${type}.url`)}
                      </a>
                    </Typography>
                    <Typography sx={{ fontFamily: 'signikalight!important' }}>
                      {t(`shareCalendar.instructions.${type}.remark`)}
                    </Typography>
                  </TabPanel>
                ))}
              </Box>
            </StyledTabs>
          </Box>
        </Box>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleClose}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShareCalendarDialog;
